import { IUserInfoDto } from "@kuika/kuika-cl-model";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { IOnboardingTooltipItem } from "../../models/onboarding-tooltip";
import OnboardingTooltipService from "../../services/onboarding-tooltip-service";
import { isOnboardingPublicRoute } from "./is-public-route";

// Onboarding tooltips are server state: one read shared by the manager, the layer and the
// screen-design listener, invalidated by the settings panel after every edit.
export const onboardingTooltipKeys = {
  all: ["onboarding-tooltips"] as const
};

// Tooltips marked as draft are only shown to the editors who author them.
const DRAFT_VIEWERS: ReadonlySet<string> = new Set<string>([
  "furkan.ceylan@kuika.com",
  "ozge.kacar@kuika.com",
  "burcu.yazici@kuika.com",
  "kadir.bayar@kuika.com"
]);

interface IWindowWithUserInfo extends Window {
  userInfo?: IUserInfoDto;
}

const EMPTY_TOOLTIP_MAP: Map<string, IOnboardingTooltipItem> = new Map<string, IOnboardingTooltipItem>();

const RETRY_COUNT: number = 3;

const STALE_TIME_MS: number = 5 * 60_000;

// Read at scan time, not fetch time: the tooltip read can start before PrivateRoutes has
// populated `window.userInfo`, but a target only reaches the DOM once the private view has
// rendered, i.e. after the user is known. Resolving it here costs no extra identity request.
export const canViewDraftTooltips = (): boolean => {
  const username: string | undefined = (window as IWindowWithUserInfo).userInfo?.username;
  return username !== undefined && DRAFT_VIEWERS.has(username);
};

// Module-level so TanStack memoises the result per data reference: an inline selector would
// hand every render a new Map and re-run the DOM scan and listener wiring that key on it.
const toTooltipMap = (tooltips: IOnboardingTooltipItem[]): Map<string, IOnboardingTooltipItem> => {
  const tooltipMap: Map<string, IOnboardingTooltipItem> = new Map<string, IOnboardingTooltipItem>();
  tooltips.forEach((tooltip: IOnboardingTooltipItem): void => {
    if (tooltip.selector) {
      tooltipMap.set(tooltip.selector, tooltip);
    }
  });
  return tooltipMap;
};

const retryDelay = (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 8000);

// The read is gated on the route, not on mount: the app boots on "/" after every sign-in and
// only then navigates into the workspace, so a mount-time check would never fetch.
export const useOnboardingTooltipsEnabled = (): boolean => {
  const { pathname } = useLocation();
  return !isOnboardingPublicRoute(pathname);
};

export const useOnboardingTooltipMap = (): Map<string, IOnboardingTooltipItem> => {
  const enabled: boolean = useOnboardingTooltipsEnabled();
  const query: UseQueryResult<Map<string, IOnboardingTooltipItem>> = useQuery({
    queryKey: onboardingTooltipKeys.all,
    queryFn: OnboardingTooltipService.getTooltips,
    select: toTooltipMap,
    enabled,
    staleTime: STALE_TIME_MS,
    retry: RETRY_COUNT,
    retryDelay,
    refetchOnWindowFocus: false,
    // A missing tooltip is not worth interrupting the user for; the retries above cover blips.
    onError: (error: unknown): void => {
      console.warn("Onboarding tooltips could not be loaded", error);
    }
  });
  return query.data ?? EMPTY_TOOLTIP_MAP;
};
